import * as Sentry from "@sentry/react";
import { z } from "zod";
import { sessionStore } from "./lib/store";

// Bundle freshness: the page finds out that it runs an old bundle, and loads
// the deployed one.
//
// The server publishes the release it deploys in `/build.json` (vite.config.ts,
// deployedBuildPlugin). The page compares it with its own release at boot and
// at each return to the foreground. A stale page reloads:
// - at boot, when build.json answers within 2 s: the user has not started to
//   type yet. A later answer waits for the next return to the foreground;
// - on a return to the foreground, only after a long background stay, so a
//   quick trip to another app never throws away a half-typed question.
//
// The reload works because navigations ask the server first (vite.config.ts,
// the NetworkFirst navigation route). It can still bring back the old bundle:
// offline, after the 3 s timeout, or under a worker from before that route,
// which answers every navigation from its precache. The page then reloads
// only one time for each deployed release, and reports itself to Sentry as
// "still stale". In Nov 2026 such devices ran bundles up to 40 days old.

const RESUME_RELOAD_AFTER_MS = 10 * 60 * 1000;
const BOOT_RELOAD_WITHIN_MS = 2000;

const deployedBuildSchema = z.object({
  release: z.string(),
  builtAt: z.number(),
});

type FreshnessCheck =
  | { moment: "boot"; answeredAfterMs: number }
  | { moment: "resume"; hiddenForMs: number };

type FreshnessAction = "none" | "reload" | "report-still-stale";

export function freshnessAction(input: {
  runningRelease: string;
  deployedRelease: string;
  reloadedToward: string | null;
  check: FreshnessCheck;
}): FreshnessAction {
  if (input.deployedRelease === input.runningRelease) return "none";
  if (input.reloadedToward === input.deployedRelease)
    return "report-still-stale";
  switch (input.check.moment) {
    case "boot":
      return input.check.answeredAfterMs <= BOOT_RELOAD_WITHIN_MS
        ? "reload"
        : "none";
    case "resume":
      return input.check.hiddenForMs >= RESUME_RELOAD_AFTER_MS
        ? "reload"
        : "none";
    default:
      throw new Error(input.check satisfies never);
  }
}

type BundleLag =
  | "current"
  | "under 1 day"
  | "1 to 3 days"
  | "3 to 7 days"
  | "over 7 days";

/** How much older the running bundle is than the deployed one. */
export function bundleLag(
  runningBuiltAt: number,
  deployedBuiltAt: number
): BundleLag {
  const days = (deployedBuiltAt - runningBuiltAt) / (24 * 60 * 60 * 1000);
  if (days <= 0) return "current";
  if (days < 1) return "under 1 day";
  if (days < 3) return "1 to 3 days";
  if (days < 7) return "3 to 7 days";
  return "over 7 days";
}

const reloadedTowardStore = sessionStore(
  "bundle-freshness-reloaded-toward",
  null,
  z.string().nullable()
);
const stillStaleReportedStore = sessionStore(
  "bundle-freshness-still-stale-reported",
  false,
  z.boolean()
);

async function fetchDeployedBuild(): Promise<z.infer<
  typeof deployedBuildSchema
> | null> {
  let response: Response;
  try {
    response = await fetch("/build.json", { cache: "no-store" });
  } catch {
    // Offline: the next return to the foreground checks again.
    return null;
  }
  if (!response.ok)
    throw new Error(`build.json answered HTTP ${response.status}`);
  return deployedBuildSchema.parse(await response.json());
}

async function checkFreshness(
  running: { release: string; builtAt: number },
  trigger: { moment: "boot" } | { moment: "resume"; hiddenForMs: number }
): Promise<void> {
  const startedAt = Date.now();
  const deployed = await fetchDeployedBuild();
  if (deployed === null) return;
  const check: FreshnessCheck =
    trigger.moment === "boot"
      ? { moment: "boot", answeredAfterMs: Date.now() - startedAt }
      : trigger;

  const lag = bundleLag(running.builtAt, deployed.builtAt);
  Sentry.setTag("bundle_lag", lag);

  const action = freshnessAction({
    runningRelease: running.release,
    deployedRelease: deployed.release,
    reloadedToward: reloadedTowardStore.get(),
    check,
  });
  switch (action) {
    case "none":
      return;
    case "reload":
      reloadedTowardStore.set(deployed.release);
      globalThis.location.reload();
      return;
    case "report-still-stale":
      if (stillStaleReportedStore.get()) return;
      stillStaleReportedStore.set(true);
      Sentry.captureMessage("Bundle still stale after a reload", {
        level: "warning",
        tags: { bundle_lag: lag, deployed_release: deployed.release },
      });
      return;
    default:
      throw new Error(action satisfies never);
  }
}

export function startBundleFreshness(running: {
  release: string;
  builtAt: number;
}): void {
  void checkFreshness(running, { moment: "boot" });

  let hiddenAt: number | null = null;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }
    if (hiddenAt === null) return;
    const hiddenForMs = Date.now() - hiddenAt;
    hiddenAt = null;
    void checkFreshness(running, { moment: "resume", hiddenForMs });
  });
}
